import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import React, { useState } from "react";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    imageIP = "imageIP",
    contentIP = "contentIP",
    contentIPMobile = "contentIPMobile",
    buttonLabelIP = "buttonLabelIP",
    imageAD = "imageAD",
    contentAD = "contentAD",
    contentADMobile = "contentADMobile",
    buttonLabelAD = "buttonLabelAD",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.imageIP]: string;
        [fields.contentIP]: string;
        [fields.contentIPMobile]: string;
        [fields.buttonLabelIP]: string;
        [fields.imageAD]: string;
        [fields.contentAD]: string;
        [fields.contentADMobile]: string;
        [fields.buttonLabelAD]: string;
    };
    extendedStyles?: CustomerContentStyles;
}

export interface CustomerContentStyles {
    wrapper?: InjectableCss;
    topSection?: InjectableCss;
    title?: TypographyProps;
    subtitle?: TypographyProps;
    bottomSection?: InjectableCss;
    contentWrapper?: InjectableCss;
    buttonSection?: InjectableCss;
    description?: TypographyProps;
    lightButton?: ButtonProps;
    lightButtonActive?: ButtonProps;
    content?: TypographyProps;
    registerButton?: ButtonProps;
    registerButtonText?: TypographyProps;
}

export const applicationsStyles: CustomerContentStyles = {
    wrapper: {
        css: css`
            width: 100%;
            color: ${UCSColors.gray03};
            background-color: ${UCSColors.gray10};
        `,
    },
    topSection: {
        css: css`
            width: 100%;
            padding: 64px 45px 48px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 20px 40px;
                    `,
                    css`
                        padding: 32px 20px 40px;
                    `,
                ]);
            }}
        `,
    },
    title: {
        as: "h1",
        weight: 400,
        size: 64,
        color: "primary",
        css: css`
            letter-spacing: -0.05em;
            line-height: 72px;
            margin-bottom: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 36px;
                        line-height: 36px;
                        letter-spacing: -0.02em;
                        margin-bottom: 8px;
                    `,
                    css`
                        font-size: 36px;
                        line-height: 36px;
                        letter-spacing: -0.02em;
                        margin-bottom: 8px;
                    `,
                ]);
            }}
        `,
    },
    subtitle: {
        as: "p",
        weight: 400,
        size: 22,
        css: css`
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 20px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 20px;
                        line-height: 24px;
                    `,
                ]);
            }}
        `,
    },
    bottomSection: {
        css: css`
            position: relative;
            width: 100%;
            height: 700px;
            color: ${UCSColors.gray10};
            background-color: ${UCSColors.gray01};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 720px;
                    `,
                    css`
                        height: 685px;
                    `,
                ]);
            }}
            @media only screen and (max-width: 500px) {
                height: 770px;
            }
            @media only screen and (max-width: 412px) {
                height: 810px;
            }
            @media only screen and (max-width: 390px) {
                height: 880px;
            }
            @media only screen and (max-width: 370px) {
                height: 920px;
            }
            @media only screen and (max-width: 350px) {
                height: 1020px;
            }
        `,
    },
    contentWrapper: {
        css: css`
            position: absolute;
            background: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.85) 100%);
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            padding: 0 45px 28px;
            display: flex;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px 46px;
                    `,
                    css`
                        padding: 0 20px 56px;
                    `,
                ]);
            }};
        `,
    },
    buttonSection: {
        css: css`
            width: fit-content;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-wrap: nowrap;
                    `,
                    css`
                        display: flex;
                        flex-wrap: nowrap;
                    `,
                ]);
            }};
        `,
    },
    lightButton: {
        hoverStyle: { backgroundColor: "rgba(255, 255, 255, 0.10)" },
        activeStyle: { backgroundColor: "rgba(255, 255, 255, 0.15)" },
        typographyProps: {
            size: 13,
            weight: 800,
            css: css`
                letter-spacing: 0.1em;
            `,
        },
        css: css`
            flex: 1;
            min-height: 36px;
            text-align: left;
            background-color: rgba(255, 255, 255, 0.25);
            padding: 10px 20px;
            margin: 0;
            border: none;
            transition: all 0.3s ease-in-out;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                ]);
            }}
        `,
    },
    lightButtonActive: {
        hoverStyle: { backgroundColor: UCSColors.gray10 },
        activeStyle: { backgroundColor: UCSColors.gray10 },
        typographyProps: {
            size: 13,
            weight: 800,
            css: css`
                letter-spacing: 0.1em;
            `,
        },
        css: css`
            flex: 1;
            min-height: 36px;
            text-align: left;
            color: ${UCSColors.gray00};
            background-color: ${UCSColors.gray10};
            padding: 10px 20px;
            margin: 0;
            border: none;
            transition: all 0.3s ease-in-out;
            cursor: default;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                ]);
            }}
        `,
    },
    content: {
        as: "div",
        css: css`
            margin-top: 60px;
            max-width: 640px;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 20px;
                    `,
                    css`
                        margin-top: 28px;
                    `,
                ]);
            }}
            .margin-row {
                margin-bottom: 25px;
            }
        `,
    },
    registerButton: {
        variant: "primary",
        sizeVariant: "large",
        typographyProps: {
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                    ]);
                }};
            `,
        },
        css: css`
            margin-top: 35px;
            width: fit-content;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 12px 24px;
                    `,
                    css`
                        padding: 12px 24px;
                    `,
                ]);
            }};
        `,
    },
    registerButtonText: {
        as: "span",
        css: css`
            font-size: 20px;
            text-transform: uppercase;
            font-weight: 500;
        `,
    },
};

type Props = OwnProps;

const ImageWrapper = styled("div")<{ bgImage: string }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.85) 100%), url("${p => p.bgImage}");
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    transition: opacity 0.5s ease-in-out;
`;

enum CustomerType {
    IP = "IP",
    AD = "AD",
}

const HomeCustomerContent: React.FC<Props> = ({ fields, extendedStyles }) => {
    const [activeCustomer, setActiveCustomer] = useState(CustomerType.IP);
    const [styles] = useState(() => mergeToNew(applicationsStyles, extendedStyles));

    const contentIP: string | React.ReactNode | undefined = parse(fields.contentIP || "");
    const contentIPMobile: string | React.ReactNode | undefined = parse(fields.contentIPMobile || "");
    const contentAD: string | React.ReactNode | undefined = parse(fields.contentAD || "");
    const contentADMobile: string | React.ReactNode | undefined = parse(fields.contentADMobile || "");

    const getButtonStyles = (customer: CustomerType) => {
        return activeCustomer === customer ? styles.lightButtonActive : styles.lightButton;
    };
    const imageContainerStyles = {
        css: css`
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            position: absolute;
            overflow: hidden;
        `,
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.topSection}>
                <Typography {...styles.title}>{fields.title}</Typography>
                <Typography {...styles.subtitle}>{fields.subtitle}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomSection}>
                <FitImage
                    image={fields.imageIP}
                    focalPoint="center"
                    imageExtendedStyles={{ opacity: activeCustomer === CustomerType.IP ? 1 : 0 }}
                    containerStyles={imageContainerStyles}
                />
                <FitImage
                    image={fields.imageAD}
                    focalPoint="center"
                    imageExtendedStyles={{ opacity: activeCustomer === CustomerType.AD ? 1 : 0 }}
                    containerStyles={imageContainerStyles}
                />
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.buttonSection}>
                        <Button
                            {...getButtonStyles(CustomerType.IP)}
                            onClick={() => setActiveCustomer(CustomerType.IP)}
                        >
                            {translate("Industry Professionals")}
                        </Button>
                        <Button
                            {...getButtonStyles(CustomerType.AD)}
                            onClick={() => setActiveCustomer(CustomerType.AD)}
                        >
                            {translate("Architects & Designers")}
                        </Button>
                    </StyledWrapper>
                    <Hidden below="md">
                        <Typography {...styles.content}>
                            {activeCustomer === CustomerType.IP ? contentIP : contentAD}
                        </Typography>
                    </Hidden>
                    <Hidden above="sm">
                        <Typography {...styles.content}>
                            {activeCustomer === CustomerType.IP ? contentIPMobile : contentADMobile}
                        </Typography>
                    </Hidden>
                    <Button
                        {...styles.registerButton}
                        onClick={() => {
                            window.location.href =
                                activeCustomer === CustomerType.AD ? "/catalog/application" : "/catalog/type";
                        }}
                    >
                        {activeCustomer === CustomerType.AD ? (
                            <Typography {...styles.registerButtonText}>{fields.buttonLabelAD}</Typography>
                        ) : (
                            <Typography {...styles.registerButtonText}>{fields.buttonLabelIP}</Typography>
                        )}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const tabGeneral = { displayName: "General", sortOrder: 0 };
const tabIP = { displayName: "Industry Professionals", sortOrder: 1 };
const tabAD = { displayName: "Architects & Designers", sortOrder: 2 };

const widgetModule: WidgetModule = {
    component: HomeCustomerContent,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabGeneral,
            },
            {
                name: fields.subtitle,
                displayName: "Subtitle",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabGeneral,
            },
            {
                name: fields.imageIP,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: tabIP,
            },
            {
                name: fields.contentIP,
                displayName: "Description text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabIP,
            },
            {
                name: fields.contentIPMobile,
                displayName: "Description text for mobile",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabIP,
            },
            {
                name: fields.buttonLabelIP,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Explore our Products",
                fieldType: "Translatable",
                tab: tabIP,
            },
            {
                name: fields.imageAD,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: tabAD,
            },
            {
                name: fields.contentAD,
                displayName: "Description text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabAD,
            },
            {
                name: fields.contentADMobile,
                displayName: "Description text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabAD,
            },
            {
                name: fields.buttonLabelAD,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Explore Products by Application",
                fieldType: "Translatable",
                tab: tabAD,
            },
        ],
    },
};

export default widgetModule;
